/**
 * AssessmentCard Component
 * Displays a single assessment card with full actions
 * Extracted from Assessments.js for better code organization
 */
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Brain, Send, Trash2, Eye, CheckCircle, Clock, AlertCircle,
  FileSpreadsheet, Edit, Loader2, Download, Users, BarChart3
} from 'lucide-react';

/**
 * Get the appropriate status badge for an assessment
 */
const getStatusBadge = (status, isExpired, dataFim) => {
  let effectiveStatus = status;
  if (dataFim && (status === 'ativa' || status === 'active')) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(dataFim);
    deadline.setHours(0, 0, 0, 0);
    if (deadline < today) {
      effectiveStatus = 'expirada';
    }
  }
  
  const badges = {
    rascunho: <Badge className="bg-gray-500">Rascunho</Badge>,
    ativa: <Badge className="bg-emerald-500">Ativa</Badge>,
    active: <Badge className="bg-emerald-500">Ativa</Badge>,
    encerrada: <Badge className="bg-slate-500">Encerrada</Badge>,
    expirada: <Badge className="bg-red-500 animate-pulse">Vencida</Badge>
  };
  return badges[effectiveStatus] || badges.rascunho;
};

/**
 * Check if an assessment is expired
 */
const isAssessmentExpired = (assessment) => {
  if (assessment.is_expired || assessment.status === 'expirada') return true;
  const dataFim = assessment.data_fim;
  if (!dataFim) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(dataFim);
  deadline.setHours(0, 0, 0, 0);
  return deadline < today;
};

const AssessmentCard = ({
  assessment,
  onSend,
  onDownloadPDF,
  onExportResponses,
  onViewParticipation,
  onViewCopsoqResults,
  onViewResults,
  onEdit,
  onDelete,
  loadingCopsoqResults
}) => {
  const isCopsoq = assessment.tipo === 'copsoq_ii';
  const expired = isAssessmentExpired(assessment);
  const questionCount = (assessment.questions || assessment.perguntas)?.length || 0;
  const title = assessment.title || assessment.titulo;
  const description = assessment.description || assessment.descricao || 'Sem descrição';

  return (
    <Card 
      className={`p-6 ${expired ? 'border-l-4 border-l-red-500 bg-red-50/30 dark:bg-red-950/20' : ''}`} 
      data-testid={`assessment-${assessment.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Header with title and badges */}
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h3>
            {getStatusBadge(assessment.status, assessment.is_expired, assessment.data_fim)}
            {isCopsoq && (
              <Badge className="bg-purple-500 text-white">COPSOQ II</Badge>
            )}
          </div>
          
          {/* Description */}
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {description}
          </p>
          
          {/* Expired Warning */}
          {expired && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center gap-2">
              <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                Esta avaliação venceu em {assessment.data_fim ? new Date(assessment.data_fim).toLocaleDateString('pt-BR') : 'data não definida'}. 
                Funcionários não podem mais responder.
              </span>
            </div>
          )}
          
          {/* Stats row */}
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span>{questionCount} perguntas</span>
            <span>Enviados: {assessment.total_enviados || 0}</span>
            <span>Respondidos: {assessment.total_respondidos || 0}</span>
            {assessment.total_enviados > 0 && (
              <span className="font-semibold text-emerald-600">
                Taxa: {((assessment.total_respondidos || 0) / assessment.total_enviados * 100).toFixed(1)}%
              </span>
            )}
            {assessment.data_fim && (
              <span className={expired ? 'text-red-600 font-semibold' : ''}>
                Prazo: {new Date(assessment.data_fim).toLocaleDateString('pt-BR')}
                {expired && ' (Vencido)'}
              </span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Send Button */}
          <Button
            size="sm"
            className={assessment.status === 'rascunho' 
              ? "bg-emerald-500 hover:bg-emerald-600" 
              : "bg-[#0d9488] hover:bg-[#0f766e]"
            }
            onClick={() => onSend(assessment.id)}
            data-testid={`send-${assessment.id}`}
            title={assessment.status === 'rascunho' ? "Enviar para funcionários" : "Reenviar para mais funcionários"}
          >
            <Send size={16} className="mr-1" />
            {assessment.status === 'rascunho' ? 'Enviar' : 'Reenviar'}
          </Button>
          
          {/* Download PDF */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDownloadPDF(assessment.id)}
            title="Baixar PDF"
            data-testid={`download-pdf-${assessment.id}`}
          >
            <Download size={16} />
          </Button>
          
          {/* Export Responses (Excel) - only if has responses */}
          {assessment.total_respondidos > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExportResponses(assessment.id, title)}
              title="Exportar Respostas (Excel)"
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              data-testid={`export-responses-${assessment.id}`}
            >
              <FileSpreadsheet size={16} />
            </Button>
          )}
          
          {/* View Participation */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onViewParticipation(assessment)}
            title="Ver Participação"
            data-testid={`view-participation-${assessment.id}`}
          >
            <Users size={16} />
          </Button>
          
          {/* View Results - COPSOQ or Regular */}
          {isCopsoq ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewCopsoqResults(assessment.id)}
              disabled={loadingCopsoqResults}
              title="Ver Resultados COPSOQ II"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid={`view-copsoq-${assessment.id}`}
            >
              {loadingCopsoqResults ? <Loader2 size={16} className="animate-spin" /> : <Brain size={16} />}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewResults(assessment)}
              title="Ver Resultados"
              data-testid={`view-results-${assessment.id}`}
            >
              <BarChart3 size={16} />
            </Button>
          )}
          
          {/* Edit */}
          <Button
            size="sm"
            variant="ghost"
            className="text-blue-500 hover:text-blue-600"
            onClick={() => onEdit(assessment)}
            title="Editar"
            data-testid={`edit-${assessment.id}`}
          >
            <Edit size={16} />
          </Button>
          
          {/* Delete */}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-500 hover:text-red-600"
            onClick={() => onDelete(assessment.id)}
            title="Excluir"
            data-testid={`delete-${assessment.id}`}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    </Card>
  );
};

// Export helper functions for use in parent component
export { isAssessmentExpired, getStatusBadge };
export default AssessmentCard;
